import React, { useState, useEffect } from "react";
import { FaUser } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { userStore } from "../../store/userStore";
import { getCurrentUser } from "../../api/authService";
import apiClient from "../../api/axios";

const Profile = () => {
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    profileImage: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      console.log("=== STARTING PROFILE DATA FETCH ===");
      setLoading(true);
      setError(null);
      
      // First, try to get data from userStore immediately
      const userStoreData = userStore.getState();
      console.log("UserStore data:", userStoreData);
      
      // Also check localStorage
      const storedUserData = localStorage.getItem('user-storage');
      console.log("localStorage data:", storedUserData);
      
      // Try to get data from any available source
      let userData = null;
      
      // 1. Try userStore first
      if (userStoreData && (userStoreData.name || userStoreData.email)) {
        userData = userStoreData;
        console.log("Using userStore data:", userData);
      }
      // 2. Try localStorage
      else if (storedUserData) {
        try {
          const parsedData = JSON.parse(storedUserData);
          userData = parsedData.state || parsedData;
          console.log("Using localStorage data:", userData);
        } catch (e) {
          console.error("Error parsing localStorage:", e);
        }
      }
      
      // If we have data, use it immediately
      if (userData && (userData.name || userData.email || userData.fullName)) {
        console.log("Setting profile data from cache:", userData);
        setProfileData({
          name: userData.name || userData.fullName || "User",
          email: userData.email || "user@example.com",
          phone: userData.phone || "",
          location: userData.addresses && userData.addresses.length > 0 ? 
            `${userData.addresses[0].city || ""}, ${userData.addresses[0].state || ""}` : "India",
          profileImage: userData.profileImage || "https://img.freepik.com/premium-vector/gold-membership-icon-default-avatar-profile-icon-membership-icon-social-media-user-image-vector-illustration_561158-4191.jpg?w=900",
        });
        setLoading(false);
        setError("Using cached data. Some features may not be up to date.");
        return;
      }
      
      // If no cached data, try API
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("No authentication token found. Please login again.");
          setLoading(false);
          return;
        }

        console.log("Fetching from API...");
        const response = await apiClient.get("/auth/profile");
        console.log("API response:", response.data);
        
        if (response.data && response.data.success && response.data.user) {
          const user = response.data.user;
          setProfileData({
            name: user.name || "User",
            email: user.email || "user@example.com",
            phone: user.phone || "",
            location: user.addresses && user.addresses.length > 0 ? 
              `${user.addresses[0].city || ""}, ${user.addresses[0].state || ""}` : "India",
            profileImage: user.profileImage || "https://img.freepik.com/premium-vector/gold-membership-icon-default-avatar-profile-icon-membership-icon-social-media-user-image-vector-illustration_561158-4191.jpg?w=900",
          });
        } else {
          // Fallback to default data
          setProfileData({
            name: "User",
            email: "user@example.com",
            phone: "",
            location: "India",
            profileImage: "https://img.freepik.com/premium-vector/gold-membership-icon-default-avatar-profile-icon-membership-icon-social-media-user-image-vector-illustration_561158-4191.jpg?w=900",
          });
          setError("Using default data. Please login to see your profile.");
        }
      } catch (error) {
        console.error("API error:", error);
        // Fallback to default data
        setProfileData({
          name: "User",
          email: "user@example.com",
          phone: "",
          location: "India",
          profileImage: "https://img.freepik.com/premium-vector/gold-membership-icon-default-avatar-profile-icon-membership-icon-social-media-user-image-vector-illustration_561158-4191.jpg?w=900",
        });
        setError("Using default data. Please login to see your profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);

      const response = await apiClient.put("/auth/update", {
        name: profileData.name,
        email: profileData.email,
        phone: profileData.phone,
        profileImage: profileData.profileImage,
      });

      if (response.data && response.data.success) {
        alert("Profile updated successfully!");
        // Update the user store with new data
        const setUser = userStore.getState().setUserData;
        setUser(response.data.user);
      } else {
        setError("Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setError("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-6xl w-full bg-white shadow-xl border rounded-xl flex flex-col md:flex-row overflow-hidden">
          <div className="w-full md:w-1/2 p-6 sm:p-10 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading profile...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="max-w-6xl w-full bg-white shadow-xl border rounded-xl flex flex-col md:flex-row overflow-hidden">
        {/* Left image */}
        <div className="md:w-1/2 hidden md:block">
          <img
            src="https://manubhai.in/SocialMedia/post_artworks/DGBD00687.jpg"
            alt="Jewelry Visual"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Profile form */}
        <div className="w-full md:w-1/2 p-6 sm:p-10 relative">
          {/* Top icons */}
          <div className="flex justify-between items-center mb-4">
            <FaUser className="text-xl text-gray-500" />
            <IoClose className="text-2xl text-gray-800 cursor-pointer hover:text-red-500" />
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              <div className="flex justify-between items-center">
                <span>{error}</span>
                {error.includes("Failed to fetch") && (
                  <button
                    onClick={() => window.location.reload()}
                    className="ml-2 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                  >
                    Retry
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Debug info */}
          <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded">
            <div className="text-sm">
              <strong>Debug Info:</strong>
              <br />
              Name: {profileData.name || "Empty"}
              <br />
              Email: {profileData.email || "Empty"}
              <br />
              Phone: {profileData.phone || "Empty"}
              <br />
              <div className="mt-2 space-x-2">
                <button
                  onClick={() => {
                    const userStoreData = userStore.getState();
                    console.log("Current userStore data:", userStoreData);
                    alert(`UserStore data: ${JSON.stringify(userStoreData, null, 2)}`);
                  }}
                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                >
                  Check UserStore Data
                </button>
                <button
                  onClick={() => {
                    console.log("Force refreshing profile data...");
                    window.location.reload();
                  }}
                  className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                >
                  Force Refresh
                </button>
                <button
                  onClick={() => {
                    console.log("Setting manual test data...");
                    setProfileData({
                      name: "Test User",
                      email: "test@example.com",
                      phone: "+1234567890",
                      location: "Test City, Test State",
                      profileImage: "https://img.freepik.com/premium-vector/gold-membership-icon-default-avatar-profile-icon-membership-icon-social-media-user-image-vector-illustration_561158-4191.jpg?w=900",
                    });
                    setError("Using test data for demonstration.");
                  }}
                  className="px-3 py-1 bg-purple-500 text-white text-sm rounded hover:bg-purple-600"
                >
                  Set Test Data
                </button>
              </div>
            </div>
          </div>

          {/* Profile image and name */}
          <div className="flex items-center space-x-4 mb-6">
            <img
              src={profileData.profileImage}
              alt="User"
              className="w-16 h-16 rounded-full border"
            />
            <div>
              <h3 className="font-semibold text-lg">{profileData.name}</h3>
              <p className="text-sm text-gray-500">{profileData.email}</p>
            </div>
          </div>

          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                name="name"
                value={profileData.name}
                onChange={handleChange}
                className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-purple-500"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Email account
              </label>
              <input
                type="email"
                name="email"
                value={profileData.email}
                onChange={handleChange}
                className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-purple-500"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Mobile number
              </label>
              <input
                type="text"
                name="phone"
                value={profileData.phone}
                placeholder="Add number"
                onChange={handleChange}
                className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-purple-500"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={profileData.location}
                onChange={handleChange}
                className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-purple-500"
              />
            </div>

            <button
              type="submit"
              disabled={saving}
              className={`${
                saving 
                  ? "bg-gray-400 cursor-not-allowed" 
                  : "bg-[#3A1E1E] hover:bg-[#2a1313]"
              } text-white py-2 px-6 rounded-md transition`}
            >
              {saving ? "Saving..." : "Save Change"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Profile;